import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import {
  adminGetMasterData, adminPutMasterData, adminResetMasterData, invalidateMasterData,
  type AdminBilling, type AdminSets, type Country, type Currency, type ListSet, type SetName,
} from '../../../api/masterDataApi';
import { S } from './adminPageStyles';

/** Master data (SRS #87) and country/currency configuration (SRS #86): every reference list the product uses, editable in one place. */

const LIST_SETS: { key: ListSet; label: string; hint: string }[] = [
  { key: 'industries', label: 'Industries', hint: 'Onboarding, company profile, reporting' },
  { key: 'organization_types', label: 'Organisation types', hint: 'Onboarding' },
  { key: 'job_roles', label: 'Job roles', hint: 'Onboarding' },
  { key: 'regulation_types', label: 'Regulation types', hint: 'Onboarding interests' },
  { key: 'use_cases', label: 'Use cases', hint: 'Onboarding' },
  { key: 'team_sizes', label: 'Team sizes', hint: 'Onboarding' },
  { key: 'company_sizes', label: 'Company sizes', hint: 'Company profile' },
];

const AdminMasterDataPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [sets, setSets] = useState<AdminSets['sets'] | null>(null);
  const [tab, setTab] = useState<'lists' | 'currencies' | 'countries' | 'billing'>('billing');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [billing, setBilling] = useState<AdminBilling | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminGetMasterData().then((r) => {
      setSets(r.sets);
      const d: Record<string, string> = {};
      LIST_SETS.forEach((l) => { d[l.key] = (r.sets[l.key].items as string[]).join('\n'); });
      setDraft(d);
      setCurrencies(r.sets.currencies.items as Currency[]);
      setCountries(r.sets.countries.items as Country[]);
      setBilling(r.sets.billing.items as AdminBilling);
    }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (name: SetName, value: unknown) => {
    setBusy(true); setError(null); setOk(null);
    try { await adminPutMasterData(name, value); invalidateMasterData(); setOk(`${name.replace(/_/g, ' ')} saved.`); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setBusy(false); }
  };
  const reset = async (name: SetName) => {
    if (!window.confirm(`Reset ${name.replace(/_/g, ' ')} to the shipped default?`)) return;
    setBusy(true); setError(null); setOk(null);
    try { await adminResetMasterData(name); invalidateMasterData(); setOk(`${name.replace(/_/g, ' ')} reset.`); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Reset failed'); }
    finally { setBusy(false); }
  };
  const badge = (name: SetName) => sets?.[name].overridden ? <span style={{ ...S.chip, background: 'rgba(68,76,231,0.1)', color: 'var(--ds-info-text)', marginLeft: 6 }}>customised</span> : <span style={{ ...S.chip, marginLeft: 6 }}>default</span>;

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Master Data" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Master data &amp; billing configuration</h2>
        <p style={S.sub}>The reference lists behind signup, onboarding, company profiles and reports, plus the currency and countries the platform sells in. Changes apply immediately; existing records keep the values they were saved with.</p>
        {error && <div style={S.error}>{error}</div>}
        {ok && <div style={S.ok}>{ok}</div>}
        <div style={S.tabs}>
          {(['billing', 'currencies', 'countries', 'lists'] as const).map((t) => <button key={t} style={S.tab(tab === t)} onClick={() => setTab(t)}>{t === 'lists' ? 'Reference lists' : t.charAt(0).toUpperCase() + t.slice(1)}</button>)}
        </div>

        {tab === 'billing' && billing && (
          <div style={{ maxWidth: 560, display: 'grid', gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Billing configuration {badge('billing')}</div>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 3 }}>Currency all plan prices are in
              <select style={S.input} value={billing.currency} onChange={(e) => setBilling({ ...billing, currency: e.target.value })}>
                {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name} ({c.symbol})</option>)}
              </select>
              <span style={S.muted}>Charged at Stripe/PayPal in this currency and printed on invoices. Plan prices are single numbers in this currency (see Pricing); changing it does not convert them.</span>
            </label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 3 }}>Default country
              <select style={S.input} value={billing.default_country ?? ''} onChange={(e) => setBilling({ ...billing, default_country: e.target.value || null })}>
                <option value="">—</option>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 3 }}>Tax label printed on invoices (e.g. "GST 18%", "VAT 20%")
              <input style={S.input} value={billing.tax_label} onChange={(e) => setBilling({ ...billing, tax_label: e.target.value })} placeholder="blank = not itemised" />
            </label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 3 }}>Tax rate % (informational until tax is itemised)
              <input style={S.input} type="number" min={0} max={100} step="0.01" value={billing.tax_rate_percent ?? ''} onChange={(e) => setBilling({ ...billing, tax_rate_percent: e.target.value === '' ? null : Number(e.target.value) })} />
            </label>
            <label style={{ fontSize: 12.5 }}><input type="checkbox" checked={billing.restrict_to_enabled_countries} onChange={(e) => setBilling({ ...billing, restrict_to_enabled_countries: e.target.checked })} /> Only sell to countries enabled in the Countries tab (checkout refused elsewhere)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.primary} disabled={busy} onClick={() => save('billing', billing)}>Save</button>
              <button style={S.btn} disabled={busy} onClick={() => reset('billing')}>Reset to default</button>
            </div>
          </div>
        )}

        {tab === 'currencies' && (
          <div style={{ maxWidth: 640 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Currencies {badge('currencies')}</div>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Code</th><th style={S.th}>Symbol</th><th style={S.th}>Name</th><th style={S.th}></th></tr></thead>
              <tbody>{currencies.map((c, i) => (
                <tr key={i}>
                  <td style={S.td}><input style={{ ...S.input, minWidth: 70 }} value={c.code} onChange={(e) => setCurrencies((xs) => xs.map((x, j) => (j === i ? { ...x, code: e.target.value.toUpperCase() } : x)))} /></td>
                  <td style={S.td}><input style={{ ...S.input, minWidth: 60 }} value={c.symbol} onChange={(e) => setCurrencies((xs) => xs.map((x, j) => (j === i ? { ...x, symbol: e.target.value } : x)))} /></td>
                  <td style={S.td}><input style={S.input} value={c.name} onChange={(e) => setCurrencies((xs) => xs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></td>
                  <td style={S.td}><button style={S.danger} onClick={() => setCurrencies((xs) => xs.filter((_, j) => j !== i))}>remove</button></td>
                </tr>
              ))}</tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button style={S.btn} onClick={() => setCurrencies((xs) => [...xs, { code: '', symbol: '', name: '' }])}>+ Add</button>
              <button style={S.primary} disabled={busy} onClick={() => save('currencies', currencies)}>Save</button>
              <button style={S.btn} disabled={busy} onClick={() => reset('currencies')}>Reset to default</button>
            </div>
          </div>
        )}

        {tab === 'countries' && (
          <div style={{ maxWidth: 760 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Countries {badge('countries')}</div>
            <p style={S.sub}>Disabled countries are hidden from forms; checkout is refused there only when the restriction in Billing is on.</p>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Code</th><th style={S.th}>Name</th><th style={S.th}>Currency</th><th style={S.th}>Enabled</th><th style={S.th}></th></tr></thead>
              <tbody>{countries.map((c, i) => (
                <tr key={i}>
                  <td style={S.td}><input style={{ ...S.input, minWidth: 60 }} value={c.code} onChange={(e) => setCountries((xs) => xs.map((x, j) => (j === i ? { ...x, code: e.target.value.toUpperCase() } : x)))} /></td>
                  <td style={S.td}><input style={S.input} value={c.name} onChange={(e) => setCountries((xs) => xs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></td>
                  <td style={S.td}><select style={{ ...S.input, minWidth: 90 }} value={c.currency} onChange={(e) => setCountries((xs) => xs.map((x, j) => (j === i ? { ...x, currency: e.target.value } : x)))}>{currencies.map((cu) => <option key={cu.code} value={cu.code}>{cu.code}</option>)}</select></td>
                  <td style={S.td}><input type="checkbox" checked={c.enabled !== false} onChange={(e) => setCountries((xs) => xs.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} /></td>
                  <td style={S.td}><button style={S.danger} onClick={() => setCountries((xs) => xs.filter((_, j) => j !== i))}>remove</button></td>
                </tr>
              ))}</tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button style={S.btn} onClick={() => setCountries((xs) => [...xs, { code: '', name: '', currency: billing?.currency || 'USD', enabled: true }])}>+ Add</button>
              <button style={S.primary} disabled={busy} onClick={() => save('countries', countries)}>Save</button>
              <button style={S.btn} disabled={busy} onClick={() => reset('countries')}>Reset to default</button>
            </div>
          </div>
        )}

        {tab === 'lists' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 14 }}>
            {LIST_SETS.map((l) => (
              <div key={l.key} style={{ border: '1px solid var(--ds-border)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{l.label} {badge(l.key)}</div>
                <div style={{ ...S.muted, fontSize: '0.78rem', marginBottom: 6 }}>{l.hint} · one per line</div>
                <textarea style={{ ...S.input, width: '100%', minHeight: 150, font: 'inherit', fontSize: '0.84rem' }} value={draft[l.key] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [l.key]: e.target.value }))} />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button style={S.primary} disabled={busy} onClick={() => save(l.key, (draft[l.key] || '').split('\n').map((x) => x.trim()).filter(Boolean))}>Save</button>
                  <button style={S.btn} disabled={busy} onClick={() => reset(l.key)}>Reset</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminMasterDataPage;
